/**
 * Component Rules API service.
 *
 * Thin typed wrapper around the origami `/api/claimflow/component-rules`
 * and `/api/claimflow/component-prop-labels` endpoints. Each function maps
 * one-to-one to an endpoint exposed by the Phase 1 controllers:
 *
 * - `ComponentRulesController` — CRUD + status transitions + audit log
 * - `ComponentPropLabelsController` — list + upsert
 * - `ComponentRulesTranslateController` — NL → structured rule (stubbed for
 *   now; returns a deterministic envelope the frontend can preview)
 *
 * This service uses `adminApiClient` because every consumer today is an
 * **admin-side** page (the module edit page and the per-component config
 * page). Using `claimflowApiClient` here would be a bug — it reads its
 * auth token from `localStorage.claimflow_auth_token`, which an
 * admin-logged-in user does not have, so every request 401s and the
 * claimflow 401 interceptor reloads the page (infinite loop).
 *
 * Claimflow-runtime code (Phase 6 `withComponentRules` HOC) will fetch
 * rules directly via `claimflowApiClient` from a sibling hook so the two
 * pipelines stay independent.
 *
 * Response shapes mirror the origami JSON resources exactly (snake_case).
 * A tiny `mapFromDto` pair is provided so consumers can switch to the
 * camelCased TS types declared in `@components/shared/configurable` when
 * they need to evaluate a rule locally — the HOC in Phase 6 will use it.
 */

import { adminApiClient } from './adminApiClient';
import type {
  ComponentRule,
  ConditionGroup,
  RuleEffect,
  RuleStatus,
} from '@components/shared/configurable';

// ---------------------------------------------------------------------------
// DTOs — match origami's JsonResource output (snake_case)
// ---------------------------------------------------------------------------

/**
 * JSON shape of a `rule_json` column persisted in `component_rules`.
 *
 * This is the structural body every `ComponentRule` carries: the tree of
 * conditions and the ordered list of effects. The server validates this
 * blob with `ComponentRulePayload`, so the frontend can trust the shape
 * once it arrives — we only type it, not re-validate it.
 */
export interface ComponentRuleJsonDTO {
  conditions: ConditionGroup;
  effects: RuleEffect[];
}

/**
 * JSON Resource shape returned by `ComponentRulesController` endpoints.
 *
 * Snake-cased because the server uses Laravel's default casing. Use
 * `mapComponentRuleFromDTO` to switch to the camelCased `ComponentRule`
 * type declared in `@components/shared/configurable`.
 */
export interface ComponentRuleDTO {
  id: string;
  tenant_id: string | null;
  component_id: string;
  name: string;
  description: string | null;
  nl_input: string | null;
  rule_json: ComponentRuleJsonDTO;
  status: RuleStatus;
  priority: number;
  // Tenant-scope toggle: `'private'` rules are scoped to the creating
  // tenant; `'public'` rules are shared across all tenants. Only admin
  // tenants may set / change `'public'`; the server enforces this on both
  // create and update via the `VISIBILITY_FORBIDDEN` 403.
  visibility: 'public' | 'private';
  created_by: number | null;
  approved_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Payload for `POST /claimflow/component-rules`.
 *
 * `nl_input` is optional because admins may save a draft directly from
 * the preview modal after a successful translate call — the translate
 * response already contains the NL text.
 */
export interface CreateComponentRulePayload {
  component_id: string;
  name: string;
  description?: string | null;
  nl_input?: string | null;
  rule_json: ComponentRuleJsonDTO;
  priority?: number;
  // Optional tenant-scope selector. Defaults to `'private'` on the server
  // when omitted. Sending `'public'` requires an admin tenant; non-admin
  // tenants get a 403 with error code `VISIBILITY_FORBIDDEN`. Also
  // accepted on `UpdateComponentRulePayload` so admins can flip the scope
  // after creation.
  visibility?: 'public' | 'private';
  /**
   * Target tenant for a Private rule. Only admin-module admin users may
   * send this field. When omitted (or when the visibility is 'public'),
   * the server infers tenancy from the authenticated session.
   *
   * The server validates this against the `tenants` table and rejects a
   * non-admin caller that targets a different tenant with a 422
   * validation error (`tenant_id: "Only admin tenants may target a
   * different tenant."`). `visibility=public + tenant_id` is also
   * rejected (422). Admin callers targeting their own tenant are
   * equivalent to omitting the field.
   */
  tenant_id?: string;
  // Server rejects anything other than "draft" on create; we leave it
  // out of the type entirely so typos become compile errors.
}

/**
 * Payload for `PUT /claimflow/component-rules/{id}`.
 *
 * Every field is optional — the backend uses `sometimes|required` so
 * callers can PATCH-like a subset of fields in a single request.
 *
 * Note: `visibility` is deliberately NOT part of this payload. The
 * backend routes visibility changes through the dedicated
 * `PATCH /claimflow/component-rules/{id}/visibility` endpoint (see
 * `updateRuleVisibility` below) and silently drops the field here if
 * sent. Keep the two concerns separate so an accidental rename or
 * re-addition can't regress visibility to a silent no-op.
 */
export interface UpdateComponentRulePayload {
  name?: string;
  description?: string | null;
  nl_input?: string | null;
  rule_json?: ComponentRuleJsonDTO;
  priority?: number;
}

/**
 * JSON Resource shape returned by `ComponentPropLabelsController`.
 */
export interface ComponentPropLabelDTO {
  id: string;
  tenant_id: string | null;
  component_id: string;
  prop_name: string;
  display_label: string;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * JSON Resource shape returned by `ComponentRulesController::auditLog`.
 */
export interface ComponentRuleAuditLogDTO {
  id: string;
  tenant_id: string | null;
  user_id: number | null;
  action: string;
  component_id: string | null;
  nl_input: string | null;
  llm_output: string | null;
  validation_result: string | null;
  rule_json: ComponentRuleJsonDTO | null;
  ip_address: string | null;
  created_at: string | null;
}

/**
 * Payload for `POST /claimflow/component-rules/translate`.
 *
 * `context_variables` is the list of variable names the current page
 * allows rules to reference. `props_schema` is an optional JSON blob the
 * server can use to constrain prop names; the LLM phase will consume it.
 */
export interface TranslateComponentRulePayload {
  component_id: string;
  nl_input: string;
  context_variables?: string[];
  props_schema?: Record<string, unknown>;
  // Allow-list of prop keys that legitimately support a `filterPropValues`
  // effect (the registration's `filterableProps`). The translator prompt
  // consumes it to prefer `setProp` over `filterPropValues` for props
  // outside this list — pointing `filterPropValues` at a non-filterable prop
  // silently no-ops at runtime.
  filterable_props?: readonly string[];
}

/**
 * Envelope returned by `POST /claimflow/component-rules/translate`.
 *
 * Matches the stub controller's response shape. The `rule` sub-object
 * carries the subset of fields the frontend needs to preview before
 * writing to the database — no `id`, `createdAt`, or `updatedAt` yet.
 */
export interface TranslateComponentRuleResponse {
  rule: {
    componentId: string;
    name: string;
    description: string | null;
    nlInput: string;
    conditions: ConditionGroup;
    effects: RuleEffect[];
    status: RuleStatus;
    priority: number;
  };
  confidence: number;
  reasoning: string;
}

// ---------------------------------------------------------------------------
// DTO ↔ TS type mapping helpers
// ---------------------------------------------------------------------------

/**
 * Convert a server DTO into the camelCased `ComponentRule` type used by
 * the rule evaluator and the HOC. Drops server-only fields like
 * `tenant_id` and `created_by` so downstream code does not depend on
 * persistence details.
 */
export function mapComponentRuleFromDTO(dto: ComponentRuleDTO): ComponentRule {
  return {
    id: dto.id,
    componentId: dto.component_id,
    name: dto.name,
    description: dto.description ?? '',
    nlInput: dto.nl_input ?? '',
    conditions: dto.rule_json.conditions,
    effects: dto.rule_json.effects,
    status: dto.status,
    priority: dto.priority,
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Shape of the origami `AnonymousResourceCollection` payload used by all
 * list endpoints. The resource data always lives under `data`; the
 * audit-log endpoint additionally returns `links` / `meta` from Laravel's
 * paginator, but we only consume `data` on the frontend for now.
 */
interface CollectionResponse<T> {
  data: T[];
}

/** Shape of the single-item JSON resource payloads. */
interface SingleResourceResponse<T> {
  data: T;
}

// Base paths under the claimflow API client (which already scopes to /api).
const COMPONENT_RULES_PATH = '/claimflow/component-rules';
const COMPONENT_PROP_LABELS_PATH = '/claimflow/component-prop-labels';

/**
 * List component rules for a given component.
 *
 * `componentId` is required; `status` is optional and filters to a single
 * lifecycle bucket when provided. The admin list page uses no filter,
 * relying on the column to show status badges; the HOC (Phase 6) filters
 * to `'active'` only.
 */
async function listRules(
  componentId: string,
  options: { status?: RuleStatus } = {},
): Promise<ComponentRuleDTO[]> {
  const params: Record<string, string> = { component_id: componentId };
  if (options.status !== undefined) {
    params.status = options.status;
  }
  const response = await adminApiClient.get<
    CollectionResponse<ComponentRuleDTO>
  >(COMPONENT_RULES_PATH, { params });
  return response.data.data;
}

/**
 * List component rules without a required `componentId` filter.
 *
 * Powers the module-scoped rules table on the admin module edit page
 * (side-quest before Phase 6). Both `componentId` and `status` are
 * optional — omit the former to get every rule the backend returns for
 * the current claimflow scope.
 *
 * Kept alongside (not merged into) `listRules` so existing callers of
 * `listRules(componentId, ...)` do not have to change. The server's
 * `ComponentRulesController::index` already tolerates a missing
 * `component_id` query param, so we simply do not send it when the
 * caller did not provide one.
 *
 * TODO (multi-module): the endpoint paths are hard-coded under
 * `/claimflow/` because Phase 1 registered the backend routes under the
 * claimflow module prefix. When component rules spread beyond claimflow
 * we will need to pick a URL prefix based on the module slug (or expose
 * a cross-module aggregator endpoint on origami). `adminApiClient` will
 * work for any of those paths — it has no module-specific headers.
 */
async function listAllRules(
  options: { componentId?: string; status?: RuleStatus } = {},
): Promise<ComponentRuleDTO[]> {
  const params: Record<string, string> = {};
  if (options.componentId !== undefined) {
    params.component_id = options.componentId;
  }
  if (options.status !== undefined) {
    params.status = options.status;
  }
  const response = await adminApiClient.get<
    CollectionResponse<ComponentRuleDTO>
  >(COMPONENT_RULES_PATH, { params });
  return response.data.data;
}

/** Fetch a single rule by id. */
async function getRule(id: string): Promise<ComponentRuleDTO> {
  const response = await adminApiClient.get<
    SingleResourceResponse<ComponentRuleDTO>
  >(`${COMPONENT_RULES_PATH}/${id}`);
  return response.data.data;
}

/** Create a new draft rule. */
async function createRule(
  payload: CreateComponentRulePayload,
): Promise<ComponentRuleDTO> {
  const response = await adminApiClient.post<
    SingleResourceResponse<ComponentRuleDTO>
  >(COMPONENT_RULES_PATH, payload);
  return response.data.data;
}

/** Update an existing rule's editable fields. */
async function updateRule(
  id: string,
  payload: UpdateComponentRulePayload,
): Promise<ComponentRuleDTO> {
  const response = await adminApiClient.put<
    SingleResourceResponse<ComponentRuleDTO>
  >(`${COMPONENT_RULES_PATH}/${id}`, payload);
  return response.data.data;
}

/**
 * Transition a rule's status.
 *
 * Only the legal transitions are accepted by the server; see
 * `UpdateComponentRuleStatusRequest::withValidator` for the state machine
 * (draft → active, active → archived).
 */
async function updateRuleStatus(
  id: string,
  status: RuleStatus,
): Promise<ComponentRuleDTO> {
  const response = await adminApiClient.patch<
    SingleResourceResponse<ComponentRuleDTO>
  >(`${COMPONENT_RULES_PATH}/${id}/status`, { status });
  return response.data.data;
}

/**
 * Transition a rule's visibility.
 *
 * Dedicated endpoint, separate from the general `PUT` update: the server
 * requires admin-tenant privilege for `'public'` and responds with a 403
 * (`VISIBILITY_FORBIDDEN`) otherwise. 422 is returned when the submitted
 * value matches the current one — callers should diff client-side before
 * firing to avoid that.
 */
async function updateRuleVisibility(
  id: string,
  visibility: 'public' | 'private',
): Promise<ComponentRuleDTO> {
  const response = await adminApiClient.patch<
    SingleResourceResponse<ComponentRuleDTO>
  >(`${COMPONENT_RULES_PATH}/${id}/visibility`, { visibility });
  return response.data.data;
}

/** Permanently delete a rule. Returns `void`; server replies 204. */
async function deleteRule(id: string): Promise<void> {
  await adminApiClient.delete(`${COMPONENT_RULES_PATH}/${id}`);
}

/**
 * Translate an NL rule description into a structured preview.
 *
 * Does not persist anything — the admin reviews the result in a preview
 * modal and explicitly confirms before `createRule` is called. The stub
 * controller returns a deterministic envelope with `confidence: 0.5`;
 * real Claude integration is a follow-up phase.
 */
async function translateRule(
  payload: TranslateComponentRulePayload,
): Promise<TranslateComponentRuleResponse> {
  const response = await adminApiClient.post<
    SingleResourceResponse<TranslateComponentRuleResponse>
  >(`${COMPONENT_RULES_PATH}/translate`, payload);
  return response.data.data;
}

/** List prop labels currently stored for a component. */
async function listPropLabels(
  componentId: string,
): Promise<ComponentPropLabelDTO[]> {
  const response = await adminApiClient.get<
    CollectionResponse<ComponentPropLabelDTO>
  >(COMPONENT_PROP_LABELS_PATH, { params: { component_id: componentId } });
  return response.data.data;
}

/**
 * Upsert a single prop label for a component.
 *
 * The server stores the record keyed on
 * `(tenant_id, component_id, prop_name)`, so the same call both creates
 * and updates — no separate endpoint for each.
 */
async function upsertPropLabel(
  componentId: string,
  propName: string,
  displayLabel: string,
): Promise<ComponentPropLabelDTO> {
  const response = await adminApiClient.put<
    SingleResourceResponse<ComponentPropLabelDTO>
  >(COMPONENT_PROP_LABELS_PATH, {
    component_id: componentId,
    prop_name: propName,
    display_label: displayLabel,
  });
  return response.data.data;
}

/**
 * Fetch the audit log for a specific component.
 *
 * The server returns a Laravel paginator; we only consume `data` at this
 * stage. The config page shows at most 20 entries; tighter filtering can
 * be added later without breaking the function signature.
 */
async function listAuditLog(
  componentId: string,
): Promise<ComponentRuleAuditLogDTO[]> {
  const response = await adminApiClient.get<
    CollectionResponse<ComponentRuleAuditLogDTO>
  >(`${COMPONENT_RULES_PATH}/audit-log`, {
    params: { component_id: componentId },
  });
  return response.data.data;
}

/**
 * Flat object grouping every component-rules API call.
 *
 * Kept as a single default export so consumers can either destructure or
 * import the whole service for mocking in tests. React Query hooks in
 * `../hooks/componentRules/` wrap each of these so pages do not touch
 * this file directly.
 */
export const componentRulesService = {
  listRules,
  listAllRules,
  getRule,
  createRule,
  updateRule,
  updateRuleStatus,
  updateRuleVisibility,
  deleteRule,
  translateRule,
  listPropLabels,
  upsertPropLabel,
  listAuditLog,
};
